/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {timetoDate} from '@ion/core/src/utils/DateUtil';
import {SpreaderInfoCardProps, SpreaderInfoRow} from '@ion/dls/src/components/assetdetails/current/SpreaderInfoCard';
import {getDashedDateTime} from '@ion/dls/src/utils/ComponentUtils';
import {formatForceAmericaData} from '../../../manufacturer/spreaderUtils';
import {
  getNoValue,
  getOtherDetailsCard,
  getRemainingRows,
  optionalMeasurement,
  splitMeasurement,
  SpreaderLayout,
  TEMPERATURE_KEYS,
  toCard,
  toRow
} from './spreaderLayoutShared';

// the Spreader Type 1 layout's slots, by the reading each one shows ...
const TYPE_ONE_KEYS = {
  status        : 'spreaderStatus',
  setRate       : 'granularSetRate',
  rateIndex     : 'granularRateIndex',
  spinnerRate   : 'spinnerDialPosition',
  materialName  : 'granularMaterialName',
  mode          : 'granularLoopMode',
  granularTotal : 'granularMaterialTotal',
  prewetTotal   : 'prewetVolumeTotal',
  directTotal   : 'directLiquidTotal',
  dustTotal     : 'dustTotal'
};

// the readings the Type 1 layout's slots show ...
const TYPE_ONE_USED_KEYS = Object.values(TYPE_ONE_KEYS);

// the readings only a Type 1 message carries ...
const TYPE_ONE_ONLY_KEYS = [TYPE_ONE_KEYS.mode, TYPE_ONE_KEYS.granularTotal, TYPE_ONE_KEYS.prewetTotal];

// the spread rate setting, which stands in for a Type 1 message's missing
// mode ...
const TYPE_ONE_MODE_FALLBACK_KEY = 'granularMaterialSetting';

// the Spreader Type 2 layout's slots, by the reading each one shows ...
const TYPE_TWO_KEYS = {
  status       : 'spreaderStatus',
  setRate      : 'granularSetRate',
  spreadRate   : 'granularMaterialSetting',
  materialName : 'granularMaterialName',
  rateIndex    : 'granularRateIndex',
  spinnerRate  : 'spinnerDialPosition',
  directTotal  : 'directLiquidTotal',
  dustTotal    : 'dustTotal'
};

// the readings the Type 2 layout's slots show ...
const TYPE_TWO_USED_KEYS = Object.values(TYPE_TWO_KEYS);

// the received date and time, combined into the Conditions card's received
// time row ...
const RECEIVED_DATE_KEY  = 'date';
const RECEIVED_TIME_KEYS = [RECEIVED_DATE_KEY, 'time'];

// a Force America reading, formatted like legacy's ForceAmericaCardListContent ...
const getForceAmericaReading = (measures: GenericObject, key: string) => (
  !isNil(measures?.[key]) ? formatForceAmericaData(key, measures[key]) : undefined
);

const getForceAmericaLabel = (key: string) => translate(`tracking:spreader.FORCE_AMERICA.${key}`);

// the received time row - `date` and `time` combined like
// ForceAmericaCardListContent, or the date as is without a time - nothing
// without a date ...
const getForceAmericaReceivedTimeRow = (measures: GenericObject): SpreaderInfoRow[] => {
  let rows: SpreaderInfoRow[] = [];
  if (!isNil(measures?.date)) {
    const receivedTime = !isNil(measures.time)
      ? getDashedDateTime(timetoDate(measures.time, measures.date, 'MM/DD/YY'))
      : measures.date;
    rows = [{label: getForceAmericaLabel(RECEIVED_DATE_KEY), value: String(receivedTime)}];
  }
  return rows;
};

// the Force America readings legacy shows beyond a layout's designed slots:
// a Conditions card (road / air temp and their received time) and an Other
// Details card for anything else reported ...
const getForceAmericaExtraCards = (measures: GenericObject, usedKeys: string[]): SpreaderInfoCardProps[] => {
  const reading        = (key: string) => getForceAmericaReading(measures, key);
  const conditionsRows = [
    ...TEMPERATURE_KEYS
      .filter(function(key) {
        return !isNil(measures?.[key]);
      })
      .map(function(key) {
        return toRow(getForceAmericaLabel(key), reading(key));
      }),
    ...getForceAmericaReceivedTimeRow(measures)
  ];
  const otherRows = getRemainingRows({
    readings : measures,
    usedKeys : [...usedKeys, ...TEMPERATURE_KEYS, ...RECEIVED_TIME_KEYS],
    label    : getForceAmericaLabel,
    format   : reading
  });
  return [
    ...toCard(translate('tracking:spreader.current.conditions'), conditionsRows),
    ...getOtherDetailsCard(otherRows)
  ];
};

/**
 * Determines whether a Force America message is a Spreader Type 1 message -
 * one carrying any of the Type 1-only readings (granular mode, granular
 * total or prewet total); every other Force America message uses the Type 2
 * layout.
 *
 * ```typescript
 *
 *   import {isForceAmericaTypeOne} from './forceAmericaLayout';
 *   ...
 *
 *   const isTypeOne = isForceAmericaTypeOne(measures);
 *
 * ```
 *
 * @param   measures The Force America measures.
 * @returns          `true` for a Type 1 message, otherwise `false`.
 */
export const isForceAmericaTypeOne = (measures: GenericObject) => (
  TYPE_ONE_ONLY_KEYS.some(function(key) {
    return !isNil(measures?.[key]);
  })
);

// Spreader Type 1: Granular Set Rate / Spread Rate Index / Spinner Rate
// tiles, the Granular Info block and the Liquid Totals block. The spinner
// tile, granular total and prewet total are omitted when not reported ...
const getForceAmericaTypeOneLayout = (measures: GenericObject): SpreaderLayout => {
  const noValue      = getNoValue();
  const reading      = (key: string) => getForceAmericaReading(measures, key);
  const spinnerRate  = reading(TYPE_ONE_KEYS.spinnerRate);
  const granularMode = reading(TYPE_ONE_KEYS.mode) ?? reading(TYPE_ONE_MODE_FALLBACK_KEY);
  const tiles        = [
    {label: getForceAmericaLabel(TYPE_ONE_KEYS.setRate), ...splitMeasurement(reading(TYPE_ONE_KEYS.setRate))},
    {label: translate('tracking:spreader.current.spreadRateIndex'), ...splitMeasurement(reading(TYPE_ONE_KEYS.rateIndex))}
  ];
  if (!isNil(spinnerRate)) {
    tiles.push({label: getForceAmericaLabel(TYPE_ONE_KEYS.spinnerRate), ...splitMeasurement(spinnerRate)});
  }
  // the spread rate setting stands in for the mode only without one, so
  // it's otherwise left to the extra cards ...
  const usedKeys = [
    ...TYPE_ONE_USED_KEYS,
    ...(isNil(measures[TYPE_ONE_KEYS.mode]) ? [TYPE_ONE_MODE_FALLBACK_KEY] : [])
  ];
  return {
    status   : reading(TYPE_ONE_KEYS.status) ?? noValue,
    tiles,
    granular : {
      materialName : reading(TYPE_ONE_KEYS.materialName) ?? noValue,
      mode         : granularMode ?? noValue,
      total        : optionalMeasurement(reading(TYPE_ONE_KEYS.granularTotal))
    },
    liquidTotals : {
      prewetTotal : optionalMeasurement(reading(TYPE_ONE_KEYS.prewetTotal)),
      directTotal : splitMeasurement(reading(TYPE_ONE_KEYS.directTotal)),
      dustTotal   : splitMeasurement(reading(TYPE_ONE_KEYS.dustTotal))
    },
    cards : getForceAmericaExtraCards(measures, usedKeys)
  };
};

// Spreader Type 2 Force America: Granular Set Rate / Granular Spread Rate
// tiles, a Granular card (material name, spread rate index, spinner rate)
// and a Totals card (direct, dust) ...
const getForceAmericaTypeTwoLayout = (measures: GenericObject): SpreaderLayout => {
  const reading = (key: string) => getForceAmericaReading(measures, key);
  const tile    = (key: string) => ({label: getForceAmericaLabel(key), ...splitMeasurement(reading(key))});
  const row     = (key: string) => toRow(getForceAmericaLabel(key), reading(key));
  return {
    status : reading(TYPE_TWO_KEYS.status) ?? getNoValue(),
    tiles  : [tile(TYPE_TWO_KEYS.setRate), tile(TYPE_TWO_KEYS.spreadRate)],
    cards  : [
      {
        title      : translate('tracking:spreader.current.granular'),
        titleValue : reading(TYPE_TWO_KEYS.materialName) ?? getNoValue(),
        rows       : [
          toRow(translate('tracking:spreader.current.spreadRateIndex'), reading(TYPE_TWO_KEYS.rateIndex)),
          row(TYPE_TWO_KEYS.spinnerRate)
        ]
      },
      {
        title : translate('tracking:spreader.current.totals'),
        rows  : [row(TYPE_TWO_KEYS.directTotal), row(TYPE_TWO_KEYS.dustTotal)]
      },
      ...getForceAmericaExtraCards(measures, TYPE_TWO_USED_KEYS)
    ]
  };
};

/**
 * Maps a Force America message's measures onto the current theme Spreader
 * accordion's Type 1 or Type 2 layout, per `isForceAmericaTypeOne`.
 *
 * ```typescript
 *
 *   import {getForceAmericaLayout} from './forceAmericaLayout';
 *   ...
 *
 *   const layout = getForceAmericaLayout(measures);
 *
 * ```
 *
 * @param   measures The Force America measures.
 * @returns          The accordion layout.
 */
export const getForceAmericaLayout = (measures: GenericObject): SpreaderLayout => (
  isForceAmericaTypeOne(measures) ? getForceAmericaTypeOneLayout(measures) : getForceAmericaTypeTwoLayout(measures)
);
